import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CookieConsentContext = createContext();

const META_PIXEL_ID = '1784624072279856';

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const savedConsent = localStorage.getItem('cookieConsent');
    if (savedConsent) {
      const parsed = JSON.parse(savedConsent);
      setConsent(parsed);
      // NUR wenn analytics === true, dann Pixel laden
      if (parsed.analytics === true) {
        initializeMetaPixel();
      }
    } else {
      setShowBanner(true);
    }
  }, []);

  const initializeMetaPixel = useCallback(() => {
    // Doppelte Prüfung: Nur laden wenn Consent vorhanden
    const savedConsent = localStorage.getItem('cookieConsent');
    if (savedConsent) {
      const parsed = JSON.parse(savedConsent);
      if (parsed.analytics !== true) {
        console.log('Meta Pixel blocked - no analytics consent');
        return;
      }
    } else {
      console.log('Meta Pixel blocked - no consent given');
      return;
    }

    if (window.fbq) return;

    // Meta Pixel initialization
    (function(f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
    
    console.log('Meta Pixel initialized with analytics consent');
  }, []);

  const disableMetaPixel = useCallback(() => {
    // Deaktiviere fbq wenn vorhanden
    if (window.fbq) {
      window.fbq('consent', 'revoke');
      console.log('Meta Pixel consent revoked');
    }
    // Entferne das Script-Tag falls vorhanden
    const scripts = document.querySelectorAll('script[src*="fbevents.js"]');
    scripts.forEach(script => script.remove());
    // Lösche fbq
    if (window.fbq) {
      delete window.fbq;
      delete window._fbq;
    }
  }, []);

  const trackEvent = useCallback((eventName, params = {}) => {
    // Strenge Prüfung: Nur tracken wenn analytics explizit true ist
    if (consent?.analytics === true && window.fbq) {
      window.fbq('track', eventName, params);
      console.log(`Meta Pixel Event: ${eventName}`, params);
    }
  }, [consent]);

  const acceptAll = useCallback(() => {
    const newConsent = {
      necessary: true,
      analytics: true,
      timestamp: new Date().toISOString()
    };
    setConsent(newConsent);
    localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
    setShowBanner(false);
    initializeMetaPixel();
    // Track ViewContent after consent
    setTimeout(() => {
      if (window.fbq) {
        window.fbq('track', 'ViewContent', { content_name: 'Landing Page' });
      }
    }, 100);
  }, [initializeMetaPixel]);

  const acceptNecessary = useCallback(() => {
    const newConsent = {
      necessary: true,
      analytics: false,  // Explizit FALSE - kein Meta Pixel
      timestamp: new Date().toISOString()
    };
    setConsent(newConsent);
    localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
    setShowBanner(false);
    // Deaktiviere Meta Pixel falls es vorher geladen war
    disableMetaPixel();
    console.log('Only necessary cookies accepted - Meta Pixel disabled');
  }, [disableMetaPixel]);

  const resetConsent = useCallback(() => {
    localStorage.removeItem('cookieConsent');
    setConsent(null);
    setShowBanner(true);
    // Deaktiviere Meta Pixel beim Zurücksetzen
    disableMetaPixel();
  }, [disableMetaPixel]);

  return (
    <CookieConsentContext.Provider value={{
      consent,
      showBanner,
      acceptAll,
      acceptNecessary,
      resetConsent,
      trackEvent
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
};
